import { useState, useEffect } from 'react';

interface BeforeInstallPromptEvent extends Event {
  prompt: () => Promise<void>;
  userChoice: Promise<{ outcome: 'accepted' | 'dismissed' }>;
}

const PROMPT_DISMISSED_KEY = 'pwa-install-prompt-dismissed';
const PROMPT_DISMISSED_DATE_KEY = 'pwa-install-prompt-dismissed-date';
const DAYS_BEFORE_RESHOWING = 7;

export const usePWAInstall = () => {
  const [deferredPrompt, setDeferredPrompt] = useState<BeforeInstallPromptEvent | null>(null);
  const [canInstall, setCanInstall] = useState(false);
  const [isInstalled, setIsInstalled] = useState(false);
  const [wasDismissed, setWasDismissed] = useState(false);

  useEffect(() => {
    const isStandalone = window.matchMedia('(display-mode: standalone)').matches;
    const isIOSStandalone = (window.navigator as any).standalone === true;

    setIsInstalled(isStandalone || isIOSStandalone);

    const dismissedState = localStorage.getItem(PROMPT_DISMISSED_KEY);
    const dismissedDate = localStorage.getItem(PROMPT_DISMISSED_DATE_KEY);

    if (dismissedState === 'true' && dismissedDate) {
      const daysSinceDismissed = Math.floor(
        (Date.now() - parseInt(dismissedDate)) / (1000 * 60 * 60 * 24)
      );

      if (daysSinceDismissed < DAYS_BEFORE_RESHOWING) {
        setWasDismissed(true);
      } else {
        localStorage.removeItem(PROMPT_DISMISSED_KEY);
        localStorage.removeItem(PROMPT_DISMISSED_DATE_KEY);
        setWasDismissed(false);
      }
    }

    const handleBeforeInstallPrompt = (e: Event) => {
      e.preventDefault();
      const promptEvent = e as BeforeInstallPromptEvent;
      setDeferredPrompt(promptEvent);
      setCanInstall(true);
    };

    const handleAppInstalled = () => {
      setIsInstalled(true);
      setCanInstall(false);
      setDeferredPrompt(null);
      localStorage.removeItem(PROMPT_DISMISSED_KEY);
      localStorage.removeItem(PROMPT_DISMISSED_DATE_KEY);
    };

    window.addEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
    window.addEventListener('appinstalled', handleAppInstalled);

    return () => {
      window.removeEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
      window.removeEventListener('appinstalled', handleAppInstalled);
    };
  }, []);

  const promptInstall = async (): Promise<boolean> => {
    if (!deferredPrompt) {
      return false;
    }

    try {
      await deferredPrompt.prompt();
      const { outcome } = await deferredPrompt.userChoice;

      if (outcome === 'accepted') {
        setIsInstalled(true);
        setCanInstall(false);
        localStorage.removeItem(PROMPT_DISMISSED_KEY);
        localStorage.removeItem(PROMPT_DISMISSED_DATE_KEY);
      }

      setDeferredPrompt(null);
      return outcome === 'accepted';
    } catch (error) {
      console.error('Error showing install prompt:', error);
      return false;
    }
  };

  const dismissPrompt = () => {
    localStorage.setItem(PROMPT_DISMISSED_KEY, 'true');
    localStorage.setItem(PROMPT_DISMISSED_DATE_KEY, Date.now().toString());
    setWasDismissed(true);
  };

  return {
    canInstall,
    isInstalled,
    wasDismissed,
    promptInstall,
    dismissPrompt,
  };
};
